import React, { useEffect, useState } from "react";
import { Button, message, Row, Tag, Col } from "antd";
import { PlusOutlined, CloudDownloadOutlined } from "@ant-design/icons";
import { useHistory } from "react-router-dom";
import { CSVLink } from "react-csv";

import { useFetchConstant } from "apis/constant";
import { useGetAdvisees, patchAdvisee, refetchAdvisees } from "apis/advisee";

import Title from "components/Title/Title";
import ClientJoinDateModal from "components/ClientJoinDateModal/ClientJoinDateModal";

import {
  buildPPISummaryUri,
  buildAWPersonalUri,
  buildPoliciesUri,
} from "utils/routes";
import {
  getColumnFilterSearchProps,
  constructColumn,
  handleOnAggregationTableChange,
} from "utils/table/table";
import moment from "moment";
import { getUserAuthObj } from "utils/auth";

import { AdviseesTable } from "./Advisees.styles";
import Locale from "react-intl-universal";

const Advisees = () => {
  const history = useHistory();

  const [query, setQuery] = useState({ currentPage: 1, limit: 10 });
  const [isJoinDateModalVisible, setIsJoinDateModalVisible] = useState(false);
  const [selectedAdviseeId, setSelectedAdviseeId] = useState();

  const jwt = getUserAuthObj();

  const { paginatedData: AllPageData } = useGetAdvisees({
    currentPage: 1,
    limit: null,
  });

  const {
    isLoading: isAdviseesLoading,
    paginatedData: advisees,
    total,
  } = useGetAdvisees(query);
  const {
    isLoading: isAdviseeCategoriesLoading,
    selection: adviseeCategories,
    data: adviseeCategoriesConst,
  } = useFetchConstant("adviseeCategories");

  const [csv_data, set_csv_data] = useState([]);
  const [csv_header, set_csv_header] = useState([
    {
      label: "Creation Date",
      key: "createdAt",
    },
    {
      label: "Full Name",
      key: "name",
    },
    {
      label: "Contact Number",
      key: "contact",
    },
    {
      label: "Email",
      key: "email",
    },
    {
      label: "D.O.B.",
      key: "dob",
    },
    {
      label: "PIC",
      key: "assignedTo",
    },
    {
      label: "Category",
      key: "category",
    },
  ]);

  var GetAdvisees = useGetAdvisees({ currentPage: 1, limit: null });

  useEffect(() => {
    if (
      jwt.role === "contributor"
      // jwt.role === "agent_full_and_contributor"
    ) {
      history.push(buildPoliciesUri());
    }
  }, []);

  const generateCSV = () => {
    var order = [...GetAdvisees.paginatedData];
    var csv_data = [];
    // var sum_balance = 0;

    for (let i = 0; i < order.length; i++) {
      const real_order = order[i];
      // console.log(real_order);
      // sum_balance = parseFloat(
      //   parseFloat(sum_balance + real_order.balance).toFixed(2)
      // );

      csv_data.push({
        name: real_order.name,
        dob: real_order.dob,
        email: real_order.email,
        contact: real_order.contact,
        assignedTo: real_order.assignedTo,
        category: real_order.category,
        createdAt: real_order.createdAt,
      });
    }
    // csv_data.push({
    //   balance: sum_balance,
    // });
    set_csv_data(csv_data);
    // this.setState({
    //   csv_data,
    // });
    return true;
  };

  const columns = [
    {
      ...constructColumn(
        Locale.get("Advisees.adviseesComponent.fullName"),
        "name",
        { hasSorter: true }
      ),
      ...getColumnFilterSearchProps("name", "Search name"),
    },
    {
      ...constructColumn(Locale.get("Advisees.adviseesComponent.dob"), "dob", {
        isDate: true,
      }),
    },
    {
      ...constructColumn(
        Locale.get("Advisees.adviseesComponent.contact"),
        "contact"
      ),
    },
    {
      ...constructColumn(
        Locale.get("Advisees.adviseesComponent.pic"),
        "assignedTo",
        { hasSorter: true }
      ),
      ...getColumnFilterSearchProps("assignedTo", "Search name"),
      render: (currentAssignedTo) => currentAssignedTo || "(Not assigned)",
    },
    {
      ...constructColumn(
        Locale.get("Advisees.adviseesComponent.category"),
        "category",
        { hasSorter: true }
      ),
      render: (category) =>
        // getLabelOfConstant(category, adviseeCategories)
        {
          // console.log(category);
          if (category === "client") {
            return (
              <Tag color={"#389e0d"}>
                {Locale.get("Advisees.adviseesComponent.clientTag")}
              </Tag>
            );
          } else if (category === "prospect") {
            return Locale.get("Advisees.adviseesComponent.prospect");
          } else {
            return category;
          }
        },
    },
    {
      ...constructColumn(
        Locale.get("Advisees.adviseesComponent.category"),
        "category"
      ),
      render: (category, record) => getActionRender(record._id, category),
    },
  ];

  const updateAdviseeCategory = (selectedAdviseeId, updatePayload) => {
    patchAdvisee(selectedAdviseeId, updatePayload).then(() => {
      message.success(Locale.get("Advisees.adviseesComponent.updateSuccess"));
      setIsJoinDateModalVisible(false);
      refetchAdvisees();
      setSelectedAdviseeId();
    });
  };

  const getActionRender = (selectedAdviseeId, currentCategory) => {
    if (!isAdviseeCategoriesLoading) {
      const isClient = currentCategory === adviseeCategoriesConst.CLIENT.code;
      const categoryToUpdate = isClient
        ? adviseeCategoriesConst.PROSPECT
        : adviseeCategoriesConst.CLIENT;

      const buttonText = `${categoryToUpdate.label}`;

      return (
        <Button
          onClick={(e) => {
            e.stopPropagation();
            setSelectedAdviseeId(selectedAdviseeId);
            if (!isClient) {
              setIsJoinDateModalVisible(true);
            } else {
              updateAdviseeCategory(selectedAdviseeId, {
                category: categoryToUpdate.code,
              });
            }
          }}
        >
          {buttonText === "Client"
            ? Locale.get("Advisees.adviseesComponent.convertClient")
            : Locale.get("Advisees.adviseesComponent.convertProspect")}
        </Button>
      );
    }
  };

  const handleJoinDateModalOk = (payload) => {
    updateAdviseeCategory(selectedAdviseeId, payload);
  };

  const handleJoinDateModalCancel = () => {
    setIsJoinDateModalVisible(false);
    setSelectedAdviseeId();
  };

  const handleOnRow = (record) => ({
    onClick: () => history.push(buildPPISummaryUri(record._id)),
  });

  return (
    <div>
      <Row justify="space-between" align="middle">
        <Title>{Locale.get("Advisees.pageTitle")}</Title>

        {jwt.role === "free" && AllPageData && AllPageData.length < 20 ? (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => history.push(buildAWPersonalUri())}
          >
            {Locale.get("Advisees.addNewAdvisees")}
          </Button>
        ) : (
          jwt.role !== "free" && (
            <Row gutter={24}>
              {jwt.role === "admin" && (
                <Col>
                  <CSVLink
                    data={csv_data}
                    headers={csv_header}
                    filename={`${moment().format("DD-MMM-YYYY")}_Advisees.csv`}
                    onClick={generateCSV}
                  >
                    <Button
                      icon={
                        <CloudDownloadOutlined style={{ color: "#d7a301" }} />
                      }
                      style={{ color: "#d7a301", borderColor: "#d7a301" }}
                    >
                      {Locale.get("Advisees.csv")}
                    </Button>
                  </CSVLink>
                </Col>
              )}

              <Col>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => history.push(buildAWPersonalUri())}
                >
                  {Locale.get("Advisees.addNewAdvisees")}
                </Button>
              </Col>
            </Row>
          )
        )}
      </Row>
      <AdviseesTable
        loading={isAdviseesLoading}
        style={{ fontSize: 24 }}
        columns={columns}
        dataSource={advisees}
        onRow={handleOnRow}
        onChange={(pagination, filters, sorter) =>
          handleOnAggregationTableChange(
            { pagination, filters, sorter },
            setQuery
          )
        }
        pagination={{ total, current: query.currentPage }}
        rowKey={(record) => record._id}
        showSorterTooltip={false}
      />
      <ClientJoinDateModal
        visible={isJoinDateModalVisible}
        onOk={handleJoinDateModalOk}
        onCancel={handleJoinDateModalCancel}
      />
    </div>
  );
};

export default Advisees;
